import { t } from "i18next";
import { useContext, useEffect, useState } from "react";
import DBModal from "../../component/global/DBModal";
import DBButton from "../../component/global/DBButton";
import DBFieldBlock, { FieldHint } from "../../component/global/DBFieldBlock";
import DBInput from "../../component/global/DBInput";
import DBSelect from "../../component/global/DBSelect";
import DBSwitch from "../../component/global/DBSwitch";
import DBTextArea from "../../component/global/DBTextArea";
import DBSpinner from "../../component/global/DBSpinner";
import { ToastContext } from "../../component/global/ToastContext";
import { useRequest } from "../../hooks/helperData/useRequest";
import {
    SETTING_ITEMS_LIST,
    SETTING_ITEMS_TYPE_TREE,
    SURVEY_ITEM_TYPE_FORMS,
    SURVEY_LIST,
    surveyDetailUrl,
} from "../../helperData/ApiAddress";
import {
    CriteriaStructureEditor,
    createFormStructure,
    feedbackFormToSurveyStructure,
    serializeFormStructure,
    validateFormStructure,
} from "../../modules/form-builder";
import {
    SURVEY_SUBJECT,
    SURVEY_CHANNEL,
    accessModeOptions,
    flattenItemTypeOptions,
    surveyFormDisplayOptions,
    surveyChannelOptions,
    defaultPhoneSettings,
    SURVEY_FORM_DISPLAY_STEP,
} from "./constants";
import PhoneSettingsFields from "./PhoneSettingsFields";
import { translateServerDetail } from "./translateServerDetail";
import IncentiveQuickSetup, {
    createQuickIncentive,
    defaultQuickSetup,
} from "../../features/incentives/IncentiveQuickSetup";

const defaultForm = () => ({
    title: "",
    description: "",
    subject_type: SURVEY_SUBJECT.FREE,
    subject_title: "",
    item: null,
    item_type: null,
    access_mode: 1,
    anonymous_responses: false,
    require_captcha: false,
    consent_text: "",
    retention_days: "",
    ai_enabled: false,
    form_display_mode: SURVEY_FORM_DISPLAY_STEP,
    channel: SURVEY_CHANNEL.WEB,
    phone_settings: defaultPhoneSettings(),
});

const toSelectOptions = (rows) =>
    (Array.isArray(rows) ? rows : rows?.results || []).map((row) => ({
        value: row.id,
        label: row.name,
    }));

export default function SurveyFormModal({
    show,
    survey,
    initialStructure,
    initialForm,
    onHide,
    onSuccess,
    stacked,
    personal = false,
}) {
    const toast = useContext(ToastContext);
    const editMode = Boolean(survey?.id);

    const [form, setForm] = useState(defaultForm());
    const [structure, setStructure] = useState(createFormStructure());
    const [errors, setErrors] = useState({});
    const [itemTypeOptions, setItemTypeOptions] = useState([]);
    const [items, setItems] = useState([]);
    const [sourceForms, setSourceForms] = useState([]);
    const [sourceFormId, setSourceFormId] = useState(null);
    const [hasResponses, setHasResponses] = useState(false);
    const [quickSetup, setQuickSetup] = useState(() =>
        defaultQuickSetup("survey")
    );
    const [partialTarget, setPartialTarget] = useState(null);
    const [quickSaving, setQuickSaving] = useState(false);

    const [getSurvey, getSurveyLoading] = useRequest(
        editMode ? surveyDetailUrl(survey?.id) : "",
        "GET"
    );
    const [getItemTypes] = useRequest(SETTING_ITEMS_TYPE_TREE, "GET");
    const [getItems] = useRequest(SETTING_ITEMS_LIST, "GET");
    const [getItemTypeForms] = useRequest(SURVEY_ITEM_TYPE_FORMS, "GET");
    const [saveSurvey, saveLoading] = useRequest(
        editMode ? surveyDetailUrl(survey?.id) : SURVEY_LIST,
        editMode ? "PUT" : "POST"
    );

    useEffect(() => {
        if (!show) return;
        setErrors({});
        setSourceFormId(null);
        setItems([]);
        setSourceForms([]);
        setForm({
            ...defaultForm(),
            ...(initialForm || {}),
            ...(initialStructure?.name
                ? { title: initialStructure.name }
                : {}),
        });
        setStructure(initialStructure || createFormStructure());
        setHasResponses(false);
        setQuickSetup(defaultQuickSetup("survey"));
        setPartialTarget(null);
        if (!personal) {
            getItemTypes()
                .then((tree) => setItemTypeOptions(flattenItemTypeOptions(tree)))
                .catch(() => setItemTypeOptions([]));
        } else {
            setItemTypeOptions([]);
        }
        if (editMode) {
            getSurvey()
                .then((r) => {
                    setForm({
                        title: r.title,
                        description: r.description || "",
                        subject_type: r.subject_type,
                        subject_title: r.subject_title || "",
                        item: r.item,
                        item_type: r.item_type,
                        access_mode: r.access_mode,
                        anonymous_responses: r.anonymous_responses,
                        require_captcha: r.require_captcha,
                        consent_text: r.consent_text || "",
                        retention_days: r.retention_days ?? "",
                        ai_enabled: r.ai_enabled,
                        form_display_mode:
                            r.form_display_mode || SURVEY_FORM_DISPLAY_STEP,
                        channel: r.channel || SURVEY_CHANNEL.WEB,
                        phone_settings:
                            r.phone_settings || defaultPhoneSettings(),
                    });
                    setHasResponses(r.has_responses);
                    setStructure({
                        name: r.title,
                        criteria: (r.criteria || []).map((criterion) => ({
                            name: criterion.name,
                            weight: criterion.weight,
                            criteria_state_type:
                                criterion.criteria_state_type,
                            add_description: criterion.add_description,
                            add_upload_file: criterion.add_upload_file,
                            states: (criterion.states || []).map((state) => ({
                                name: state.name,
                                emoji: state.emoji || "",
                                value: state.value,
                            })),
                        })),
                    });
                })
                .catch((e) => {
                    const detail = translateServerDetail(
                        e.response?.data?.detail,
                        t
                    );
                    toast(
                        detail || t("survey.load.error"),
                        "danger"
                    );
                    onHide?.();
                });
        }
    }, [show]);

    useEffect(() => {
        if (!show || personal || form.subject_type !== SURVEY_SUBJECT.ITEM || !form.item_type) {
            setItems([]);
            setSourceForms([]);
            return;
        }
        getItems({ item_type: form.item_type })
            .then((r) => setItems(toSelectOptions(r)))
            .catch(() => setItems([]));
        getItemTypeForms({ item_type: form.item_type })
            .then((r) => {
                const list = Array.isArray(r) ? r : [];
                setSourceForms(
                    list.map((row) => ({
                        value: row.id,
                        label: row.name,
                        structure: row.structure,
                    }))
                );
            })
            .catch(() => setSourceForms([]));
    }, [show, form.subject_type, form.item_type]);

    const handleSubjectTypeChange = (value) => {
        setSourceFormId(null);
        setForm({
            ...form,
            subject_type: value,
            item: null,
            item_type: null,
        });
        setErrors({ ...errors, item_type: undefined, item: undefined });
    };

    const handleItemTypeChange = (value) => {
        setSourceFormId(null);
        setForm({
            ...form,
            item_type: value,
            item: null,
        });
        setErrors({ ...errors, item_type: undefined });
    };

    const handleSourceFormChange = (formId) => {
        setSourceFormId(formId || null);
        if (!formId || hasResponses) return;
        const selected = sourceForms.find((row) => row.value === formId);
        const nextStructure = feedbackFormToSurveyStructure({
            name: selected?.label,
            ...selected?.structure,
        });
        if (!nextStructure.criteria.length) {
            toast(t("survey.form.subject.source.form.empty"), "danger");
            return;
        }
        setStructure(nextStructure);
        if (!form.title.trim()) {
            setForm((current) => ({
                ...current,
                title: nextStructure.name || current.title,
            }));
        }
    };

    const validate = () => {
        const nextErrors = {};
        if (!form.title.trim()) {
            nextErrors.title = t("survey.form.name.required");
        }
        if (
            !personal &&
            form.subject_type === SURVEY_SUBJECT.ITEM &&
            !form.item_type
        ) {
            nextErrors.item_type = t("survey.form.subject.item.type.required");
        }
        setErrors(nextErrors);
        if (Object.keys(nextErrors).length) return false;
        if (!editMode || !hasResponses) {
            const structureError = validateFormStructure({
                ...structure,
                name: form.title,
            });
            if (structureError) {
                toast(structureError, "danger");
                return false;
            }
        }
        return true;
    };

    const handleSave = () => {
        if (!validate()) return;
        const payload = {
            title: form.title.trim(),
            description: form.description,
            subject_type: form.subject_type,
            subject_title:
                form.subject_type === SURVEY_SUBJECT.FREE
                    ? form.subject_title.trim()
                    : "",
            item:
                form.subject_type === SURVEY_SUBJECT.ITEM
                    ? form.item || null
                    : null,
            item_type:
                form.subject_type === SURVEY_SUBJECT.ITEM
                    ? form.item_type
                    : null,
            access_mode: personal ? 1 : form.access_mode,
            anonymous_responses: form.anonymous_responses,
            require_captcha: form.require_captcha,
            consent_text: form.consent_text,
            retention_days:
                form.retention_days === "" ? null : Number(form.retention_days),
            ai_enabled: form.ai_enabled,
            form_display_mode: form.form_display_mode,
            version: editMode ? survey.version : 1,
        };
        if (!personal) {
            payload.channel = form.channel;
            if (form.channel === SURVEY_CHANNEL.PHONE) {
                payload.phone_settings = form.phone_settings;
            }
        }
        if (personal) {
            payload.personal = true;
            payload.subject_type = SURVEY_SUBJECT.FREE;
            payload.item = null;
            payload.item_type = null;
            payload.access_mode = 1;
        }
        if (!editMode || !hasResponses) {
            payload.form_structure = serializeFormStructure({
                ...structure,
                name: form.title.trim(),
            });
        }
        saveSurvey(payload)
            .then(async (saved) => {
                const target = {
                    id: saved.id || survey?.id,
                    name: saved.title || form.title,
                    type: "survey",
                };
                if (quickSetup.enabled && !personal) {
                    setQuickSaving(true);
                    try {
                        await createQuickIncentive(quickSetup, target);
                    } catch {
                        setPartialTarget(target);
                        toast(t("incentives.quick.partialTitle"), "danger");
                        return;
                    } finally {
                        setQuickSaving(false);
                    }
                }
                toast(t("survey.save.success"), "success");
                onSuccess?.(saved, { isCreate: !editMode });
            })
            .catch((e) => {
                const detail = translateServerDetail(
                    e.response?.data?.detail,
                    t
                );
                if (e.response?.status === 409) {
                    toast(t("survey.conflict.error"), "danger");
                } else if (
                    e.response?.status === 400 &&
                    JSON.stringify(e.response?.data || {}).includes(
                        "Questions cannot be changed"
                    )
                ) {
                    toast(t("survey.structure.error"), "danger");
                } else if (detail) {
                    toast(detail, "danger");
                } else {
                    toast(t("survey.save.error"), "danger");
                }
            });
    };

    const retryQuickSetup = () => {
        setQuickSaving(true);
        createQuickIncentive(quickSetup, partialTarget)
            .then(() => {
                setPartialTarget(null);
                toast(t("survey.save.success"), "success");
                onSuccess?.(partialTarget, { isCreate: !editMode });
            })
            .catch(() => toast(t("incentives.error.publish"), "danger"))
            .finally(() => setQuickSaving(false));
    };

    return (
        <DBModal
            permanent
            stacked={stacked}
            show={show}
            minWidth={720}
            onHide={onHide}
            className="survey-form-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                closeLabel={t("survey.settings.help.close")}
                title={
                    editMode
                        ? t("survey.form.edit.title")
                        : t("survey.form.create.title")
                }
            />
            <DBModal.Content>
                {editMode && getSurveyLoading ? (
                    <DBSpinner tip={t("loading")} />
                ) : (
                    <>
                        {!editMode && (
                            <p className="survey-form-help">
                                {t("survey.form.create.intro")}
                            </p>
                        )}
                        <div className="survey-form-grid">
                            {!personal ? (
                                <div className="survey-form-grid-pair">
                                    <DBFieldBlock
                                        title={t("survey.form.name")}
                                        required
                                        hint={t("survey.form.name.hint")}
                                        error={errors.title}
                                    >
                                        <DBInput
                                            size="large"
                                            status={errors.title && "error"}
                                            value={form.title}
                                            onChange={(e) =>
                                                setForm({
                                                    ...form,
                                                    title: e.target.value,
                                                })
                                            }
                                        />
                                    </DBFieldBlock>
                                </div>
                            ) : (
                                <DBFieldBlock
                                    title={t("survey.form.name")}
                                    required
                                    hint={t("survey.form.name.hint")}
                                    error={errors.title}
                                >
                                    <DBInput
                                        size="large"
                                        status={errors.title && "error"}
                                        value={form.title}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                title: e.target.value,
                                            })
                                        }
                                    />
                                </DBFieldBlock>
                            )}
                            <DBFieldBlock
                                title={t("survey.form.subject.type")}
                                hint={t("survey.form.subject.type.hint")}
                            >
                                {personal ? (
                                    <DBInput
                                        size="large"
                                        value={t("survey.form.subject.type.free")}
                                        disabled
                                    />
                                ) : (
                                <DBSelect
                                    size="large"
                                    value={form.subject_type}
                                    onChange={handleSubjectTypeChange}
                                    options={[
                                        {
                                            value: SURVEY_SUBJECT.FREE,
                                            label: t(
                                                "survey.form.subject.type.free"
                                            ),
                                        },
                                        {
                                            value: SURVEY_SUBJECT.ITEM,
                                            label: t(
                                                "survey.form.subject.type.item"
                                            ),
                                        },
                                    ]}
                                />
                                )}
                            </DBFieldBlock>
                            {form.subject_type === SURVEY_SUBJECT.FREE ? (
                                <DBFieldBlock
                                    title={t("survey.form.subject.title")}
                                    hint={t("survey.form.subject.title.hint")}
                                    error={errors.subject_title}
                                >
                                    <DBInput
                                        size="large"
                                        status={
                                            errors.subject_title && "error"
                                        }
                                        value={form.subject_title}
                                        onChange={(e) =>
                                            setForm({
                                                ...form,
                                                subject_title: e.target.value,
                                            })
                                        }
                                    />
                                </DBFieldBlock>
                            ) : (
                                <DBFieldBlock
                                    title={t("survey.form.subject.item.type")}
                                    required
                                    hint={t(
                                        "survey.form.subject.item.type.hint"
                                    )}
                                    error={errors.item_type}
                                >
                                    <DBSelect
                                        size="large"
                                        showSearch
                                        optionFilterProp="label"
                                        status={errors.item_type && "error"}
                                        value={form.item_type}
                                        onChange={handleItemTypeChange}
                                        options={itemTypeOptions}
                                    />
                                </DBFieldBlock>
                            )}
                            {form.subject_type === SURVEY_SUBJECT.ITEM &&
                                !personal && (
                                <>
                                    <DBFieldBlock
                                        title={t("survey.form.subject.item")}
                                        hint={t("survey.form.subject.item.hint")}
                                    >
                                        <DBSelect
                                            size="large"
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            placeholder={t("form.select")}
                                            value={form.item ?? undefined}
                                            onChange={(value) =>
                                                setForm({
                                                    ...form,
                                                    item: value ?? null,
                                                })
                                            }
                                            options={items}
                                            disabled={!form.item_type}
                                        />
                                    </DBFieldBlock>
                                    <DBFieldBlock
                                        title={t(
                                            "survey.form.subject.source.form"
                                        )}
                                        hint={t(
                                            "survey.form.subject.source.form.hint"
                                        )}
                                    >
                                        <DBSelect
                                            size="large"
                                            showSearch
                                            allowClear
                                            optionFilterProp="label"
                                            placeholder={t("form.select")}
                                            value={sourceFormId ?? undefined}
                                            onChange={handleSourceFormChange}
                                            options={sourceForms}
                                            disabled={
                                                !form.item_type || hasResponses
                                            }
                                        />
                                    </DBFieldBlock>
                                </>
                            )}
                            {!personal && (
                            <div className="survey-form-grid-pair">
                                <DBFieldBlock
                                    title={t("survey.channel")}
                                    hint={t("survey.channel.hint")}
                                >
                                    <DBSelect
                                        size="large"
                                        value={form.channel}
                                        onChange={(value) =>
                                            setForm({
                                                ...form,
                                                channel: value,
                                            })
                                        }
                                        options={surveyChannelOptions()}
                                    />
                                </DBFieldBlock>
                                <DBFieldBlock
                                    title={t("survey.form.access.mode")}
                                    hint={t("survey.form.access.mode.hint")}
                                >
                                    <DBSelect
                                        size="large"
                                        value={form.access_mode}
                                        onChange={(value) =>
                                            setForm({
                                                ...form,
                                                access_mode: value,
                                            })
                                        }
                                        options={accessModeOptions()}
                                    />
                                </DBFieldBlock>
                            </div>
                            )}
                            <DBFieldBlock
                                title={t("survey.form.description")}
                                hint={t("survey.form.description.hint")}
                                span={2}
                            >
                                <DBTextArea
                                    rows={2}
                                    value={form.description}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            description: e.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                            <DBFieldBlock
                                title={t("survey.form.retention")}
                                hint={t("survey.form.retention.hint")}
                            >
                                <DBInput
                                    type="number"
                                    size="large"
                                    value={form.retention_days}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            retention_days: e.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                            <DBFieldBlock
                                title={t("survey.form.consent")}
                                hint={t("survey.form.consent.hint")}
                                span={2}
                            >
                                <DBTextArea
                                    rows={2}
                                    value={form.consent_text}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            consent_text: e.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                        </div>
                        <div className="survey-form-toggles">
                            <label>
                                <DBSwitch
                                    checked={form.anonymous_responses}
                                    onChange={(value) =>
                                        setForm({
                                            ...form,
                                            anonymous_responses: value,
                                        })
                                    }
                                />
                                <span>{t("survey.form.anonymous")}</span>
                                <FieldHint hint={t("survey.form.anonymous.help")} />
                            </label>
                            <label>
                                <DBSwitch
                                    checked={form.require_captcha}
                                    onChange={(value) =>
                                        setForm({
                                            ...form,
                                            require_captcha: value,
                                        })
                                    }
                                />
                                <span>{t("survey.form.captcha")}</span>
                                <FieldHint hint={t("survey.form.captcha.hint")} />
                            </label>
                            <label>
                                <DBSwitch
                                    checked={form.ai_enabled}
                                    onChange={(value) =>
                                        setForm({ ...form, ai_enabled: value })
                                    }
                                />
                                <span>{t("survey.form.ai.enabled")}</span>
                                <FieldHint
                                    hint={t("survey.form.ai.enabled.hint")}
                                />
                            </label>
                        </div>
                        {form.anonymous_responses && (
                            <p className="survey-form-help">
                                {t("survey.form.anonymous.help")}
                            </p>
                        )}
                        <div className="survey-form-display-mode">
                            <DBFieldBlock
                                title={t("survey.form.display.mode")}
                                hint={t("survey.form.display.mode.hint")}
                            >
                                <DBSelect
                                    size="large"
                                    value={form.form_display_mode}
                                    onChange={(value) =>
                                        setForm({
                                            ...form,
                                            form_display_mode: value,
                                        })
                                    }
                                    options={surveyFormDisplayOptions()}
                                />
                            </DBFieldBlock>
                        </div>
                        {!personal && (
                        <IncentiveQuickSetup
                            value={quickSetup}
                            onChange={setQuickSetup}
                            targetType="survey"
                            partialFailure={Boolean(partialTarget)}
                            retrying={quickSaving}
                            onRetry={retryQuickSetup}
                        />
                        )}
                        {!personal && form.channel === SURVEY_CHANNEL.PHONE && (
                            <PhoneSettingsFields
                                value={form.phone_settings}
                                onChange={(phone_settings) =>
                                    setForm({ ...form, phone_settings })
                                }
                            />
                        )}
                        <h4 className="survey-form-questions-title">
                            {t("survey.form.questions")}
                        </h4>
                        {hasResponses ? (
                            <p className="survey-form-help">
                                {t("survey.form.questions.locked")}
                            </p>
                        ) : (
                            <CriteriaStructureEditor
                                structure={structure}
                                onChange={setStructure}
                                hideName
                                variant="survey"
                            />
                        )}
                    </>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    caption={
                        editMode
                            ? t("save")
                            : t("survey.form.save.continue")
                    }
                    onClick={handleSave}
                    loading={saveLoading || quickSaving}
                />
                <DBButton
                    caption={t("cancel")}
                    type="secondary"
                    onClick={onHide}
                />
            </DBModal.Footer>
        </DBModal>
    );
}
